import React, {useEffect, useMemo, useState} from 'react';
import moment from 'moment';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import API from '../../utils/API';
import {API_ENDPOINTS} from '../../apiConfig';
import {Link} from 'react-router-dom';
import Forms from '../../components/Forms';
import routes from '../../routesConfig';
import {useEmailQueueSummary, useInquirySummary} from '../../utils/inquirySummary';
import {useAuth} from '../../contexts/AuthContext';

const RANGE_OPTIONS = [
    {value: 'current_week', label: 'Aktuální týden'},
    {value: 'current_month', label: 'Aktuální měsíc'},
    {value: 'last_7_days', label: 'Posledních 7 dní'},
    {value: 'last_30_days', label: 'Posledních 30 dní'},
    {value: 'last_half_year', label: 'Poslední půl rok'},
    {value: 'custom', label: 'Vlastní datum'}
];

const Dashboard = () => {
    const [buybacks, setBuybacks] = useState([]);
    const [repairs, setRepairs] = useState([]);
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const inquirySummary = useInquirySummary();
    const {user} = useAuth();
    const emailSummary = useEmailQueueSummary(user?.profileList || []);

    const [rangeType, setRangeType] = useState('last_half_year');
    const [customFrom, setCustomFrom] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [customTo, setCustomTo] = useState(moment().format('YYYY-MM-DD'));

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [buybackResult, repairResult, salesResult] = await Promise.allSettled([
                    API.get(API_ENDPOINTS.buybacks.list),
                    API.get(API_ENDPOINTS.repairs.list),
                    API.get(API_ENDPOINTS.sales.list)
                ]);

                const buybackRows = buybackResult.status === 'fulfilled' ? extractRowsFromResponse(buybackResult.value) : [];
                const repairRows = repairResult.status === 'fulfilled' ? extractRowsFromResponse(repairResult.value) : [];
                const salesRows = salesResult.status === 'fulfilled' ? extractRowsFromResponse(salesResult.value) : [];

                setBuybacks(buybackRows);
                setRepairs(repairRows);
                setSales(salesRows);

                const failed = [buybackResult, repairResult, salesResult].filter((item) => item.status === 'rejected');
                if (failed.length > 0) {
                    setError(new Error('Nepodařilo se načíst část dat dashboardu.'));
                } else {
                    setError(null);
                }
            } catch (fetchError) {
                console.error(fetchError);
                setError(fetchError);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const range = useMemo(
        () => resolveDateRange(rangeType, customFrom, customTo),
        [rangeType, customFrom, customTo]
    );

    const filtered = useMemo(() => {
        const inRange = (dateValue) => isWithinRange(dateValue, range.from, range.to);
        return {
            buybacksInRange: buybacks.filter((item) => inRange(item.dealAt || item.createdAt)),
            repairsInRange: repairs.filter((item) => inRange(item.createdAt)),
            salesInRange: sales.filter((item) => inRange(item.saleDate || item.createdAt))
        };
    }, [buybacks, repairs, sales, range]);

    const metrics = useMemo(() => {
        const {buybacksInRange, repairsInRange, salesInRange} = filtered;
        const days = Math.max(1, range.to.diff(range.from, 'days') + 1);

        const buybacksTotal = buybacksInRange.length;
        const repairsTotal = repairsInRange.length;
        const buybackProfit = salesInRange
            .filter((sale) => sale.sourceType === 'buyback')
            .reduce((sum, sale) => sum + (Number(sale.margin) || 0), 0);
        const repairProfit = repairsInRange
            .filter((repair) => repair.status === 'completed' && !repair.buyback)
            .reduce((sum, repair) => sum + (Number(repair.estimatedPrice) || 0), 0);
        const pendingBuybackCapital = buybacksInRange
            .filter((item) => item.status !== 'sold' && item.intakePurpose !== 'donor')
            .reduce((sum, item) => sum + (Number(item?.economics?.purchasePrice) || 0), 0);

        return {
            buybacksTotal,
            repairsTotal,
            buybackProfit,
            repairProfit,
            pendingBuybackCapital,
            avgBuybacksPerDay: buybacksTotal / days,
            avgRepairsPerDay: repairsTotal / days
        };
    }, [filtered, range]);

    const hasAnySourceData = buybacks.length > 0 || repairs.length > 0 || sales.length > 0;
    const hasAnyFilteredData = filtered.buybacksInRange.length > 0
        || filtered.repairsInRange.length > 0
        || filtered.salesInRange.length > 0;

    const dailyProfitData = useMemo(() => {
        const dailyMap = new Map();
        const cursor = range.from.clone().startOf('day');
        const end = range.to.clone().startOf('day');
        while (cursor.isSameOrBefore(end, 'day')) {
            const key = cursor.format('YYYY-MM-DD');
            dailyMap.set(key, {
                key,
                day: cursor.format('DD.MM'),
                repairProfit: 0,
                buybackSalesProfit: 0
            });
            cursor.add(1, 'day');
        }

        filtered.repairsInRange
            .filter((repair) => repair.status === 'completed' && !repair.buyback)
            .forEach((repair) => {
                const key = moment(repair.createdAt).format('YYYY-MM-DD');
                if (!dailyMap.has(key)) return;
                dailyMap.get(key).repairProfit += Number(repair.estimatedPrice) || 0;
            });

        filtered.salesInRange
            .filter((sale) => sale.sourceType === 'buyback')
            .forEach((sale) => {
                const key = moment(sale.saleDate || sale.createdAt).format('YYYY-MM-DD');
                if (!dailyMap.has(key)) return;
                dailyMap.get(key).buybackSalesProfit += Number(sale.margin) || 0;
            });

        return Array.from(dailyMap.values());
    }, [filtered, range]);

    if (loading) {
        return <div className="text-center p-4">Načítání dashboardu...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-end justify-between gap-4">
                <div>
                    <h1 className="text-xl font-semibold">Dashboard</h1>
                    <div className="text-sm text-gray-600">
                        Období: {range.from.format('DD.MM.YYYY')} - {range.to.format('DD.MM.YYYY')}
                    </div>
                </div>
                <div className="flex items-end gap-3">
                    <div className="w-60">
                        <Forms.Select
                            label="Období"
                            value={RANGE_OPTIONS.find((item) => item.value === rangeType) || RANGE_OPTIONS[0]}
                            options={RANGE_OPTIONS}
                            onChange={(option) => setRangeType(option?.value || 'current_month')}
                        />
                    </div>
                    {rangeType === 'custom' ? (
                        <>
                            <DateField label="Od" value={customFrom} onChange={setCustomFrom}/>
                            <DateField label="Do" value={customTo} onChange={setCustomTo}/>
                        </>
                    ) : null}
                </div>
            </div>

            {error ? <div className="text-sm text-red-600">{error?.message || 'Nepodařilo se načíst data.'}</div> : null}
            {!error && hasAnySourceData && !hasAnyFilteredData ? (
                <div className="text-sm text-amber-700">
                    Pro vybrané období nejsou dostupná data. Zkuste změnit filtr období.
                </div>
            ) : null}

            {emailSummary?.problems ? (
                <Link to={routes.admin.settings.emailsFull} className="block rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 hover:bg-red-100">
                    <strong>Problém s odesíláním e-mailů:</strong>{' '}
                    {emailSummary.failed ? `${emailSummary.failed} neodeslaných` : ''}
                    {emailSummary.failed && emailSummary.stuck ? ', ' : ''}
                    {emailSummary.stuck ? `${emailSummary.stuck} čeká déle než hodinu` : ''}. Zobrazit frontu e-mailů →
                </Link>
            ) : null}

            <WebInquiryTiles summary={inquirySummary}/>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <KpiTile title="Počet výkupů" value={metrics.buybacksTotal}/>
                <KpiTile title="Počet oprav" value={metrics.repairsTotal}/>
                <KpiTile title="Průměr výkupů / den" value={metrics.avgBuybacksPerDay.toFixed(2)}/>
                <KpiTile title="Průměr oprav / den" value={metrics.avgRepairsPerDay.toFixed(2)}/>
                <KpiTile title="Zisk z výkupů" value={formatMoney(metrics.buybackProfit)} tone="success"/>
                <KpiTile title="Zisk z oprav mimo výkupy" value={formatMoney(metrics.repairProfit)} tone="info"/>
                <KpiTile title="Peníze v nedokončených výkupech" value={formatMoney(metrics.pendingBuybackCapital)} tone="warning"/>
            </div>

            <div className="border border-gray-200 rounded-lg p-4 shadow-sm">
                <div className="text-sm font-medium mb-3">
                    Celkový denní zisk (stacked): opravy + prodeje vykoupených zařízení
                </div>
                <div className="h-[380px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dailyProfitData} margin={{top: 8, right: 16, left: 8, bottom: 8}}>
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis dataKey="day"/>
                            <YAxis tickFormatter={(value) => `${Math.round(value / 1000)}k`}/>
                            <Tooltip
                                formatter={(value) => formatMoney(value)}
                                labelFormatter={(label, payload) => {
                                    const rawKey = payload?.[0]?.payload?.key;
                                    return rawKey ? moment(rawKey).format('DD.MM.YYYY') : label;
                                }}
                            />
                            <Legend/>
                            <Bar
                                dataKey="repairProfit"
                                stackId="profit"
                                name="Zisk z oprav"
                                fill="#3b82f6"
                                radius={[4, 4, 0, 0]}
                            />
                            <Bar
                                dataKey="buybackSalesProfit"
                                stackId="profit"
                                name="Zisk z prodejů výkupů"
                                fill="#10b981"
                                radius={[4, 4, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

/** Dlaždice s požadavky z webu – proklik do seznamu. */
const WebInquiryTiles = ({summary}) => {
    if (!summary) return null;
    const today = summary.today || [];
    const unconfirmedToday = today.filter((item) => item.status === 'new').length;
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to={`${routes.admin.inquiries.full}?stav=new`} className="block hover:opacity-90">
                <KpiTile
                    title="Nové požadavky z webu"
                    value={summary.newTotal}
                    tone={summary.newTotal ? 'warning' : 'default'}
                />
            </Link>
            <Link to={routes.admin.inquiries.calendarFull} className="block hover:opacity-90">
                <KpiTile
                    title={`Dnešní rezervace${unconfirmedToday ? ` (${unconfirmedToday} nepotvrzené)` : ''}`}
                    value={today.length ? today.map((item) => item.reservation?.time).join(', ') : '0'}
                    tone={today.length ? 'info' : 'default'}
                />
            </Link>
            <Link to={`${routes.admin.inquiries.full}?typ=reservation`} className="block hover:opacity-90">
                <KpiTile title="Potvrzené nadcházející rezervace" value={summary.upcomingConfirmed}/>
            </Link>
        </div>
    );
};

const DateField = ({label, value, onChange}) => (
    <label className="text-sm">
        <span className="block text-xs text-gray-600 mb-1">{label}</span>
        <input
            type="date"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md bg-white"
        />
    </label>
);

const KpiTile = ({title, value, tone = 'default'}) => {
    const toneClass = {
        default: 'border-gray-200',
        success: 'border-emerald-300 bg-emerald-50/40',
        warning: 'border-amber-300 bg-amber-50/40',
        info: 'border-blue-300 bg-blue-50/40'
    }[tone] || 'border-gray-200';

    return (
        <div className={`border rounded-lg p-4 shadow-sm ${toneClass}`}>
            <div className="text-xs text-gray-500 mb-1">{title}</div>
            <div className="text-2xl font-semibold">{value}</div>
        </div>
    );
};

function resolveDateRange(rangeType, customFrom, customTo) {
    const now = moment();
    if (rangeType === 'current_week') {
        return {from: now.clone().startOf('isoWeek'), to: now.clone().endOf('day')};
    }
    if (rangeType === 'current_month') {
        return {from: now.clone().startOf('month'), to: now.clone().endOf('day')};
    }
    if (rangeType === 'last_7_days') {
        return {from: now.clone().subtract(6, 'days').startOf('day'), to: now.clone().endOf('day')};
    }
    if (rangeType === 'last_30_days') {
        return {from: now.clone().subtract(29, 'days').startOf('day'), to: now.clone().endOf('day')};
    }
    if (rangeType === 'last_half_year') {
        return {from: now.clone().subtract(6, 'months').startOf('day'), to: now.clone().endOf('day')};
    }

    const from = customFrom ? moment(customFrom).startOf('day') : now.clone().startOf('month');
    const to = customTo ? moment(customTo).endOf('day') : now.clone().endOf('day');
    if (to.isBefore(from)) {
        return {from: to.clone().startOf('day'), to: from.clone().endOf('day')};
    }
    return {from, to};
}

function isWithinRange(dateValue, from, to) {
    if (!dateValue) return false;
    const date = moment(dateValue);
    if (!date.isValid()) return false;
    return date.isBetween(from, to, undefined, '[]');
}

function formatMoney(value) {
    const number = Number(value || 0);
    return `${number.toLocaleString('cs-CZ')} CZK`;
}

function extractRowsFromResponse(response) {
    const payload = response?.data;
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.rows)) return payload.rows;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
}

export default Dashboard;
